// pages/EventManagement.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Slide,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
  Avatar,
  LinearProgress,
  Switch,
  FormControlLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DashboardIcon from "@mui/icons-material/Dashboard";
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import api from "../../api";
import { getFullImageUrl } from "../../middleware/Imagegetter";
import { styled } from '@mui/material/styles';


const THEME_BLUE = "#0d2a44";
const THEME_GREEN = "#016f57";
const THEME_BLUE_RGB = "13, 42, 68";
const THEME_GREEN_RGB = "1, 111, 87";


const peerCampusGradient =
  `linear-gradient(135deg, ${THEME_BLUE} 0%, ${THEME_GREEN} 30%, ${THEME_GREEN} 70%, ${THEME_GREEN} 100%)`;

// Styled Components
const HeaderCard = styled(Paper)(({ theme }) => ({
  background: peerCampusGradient,
  color: "white",
  borderRadius: "24px",
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: `0 20px 40px rgba(${THEME_BLUE_RGB}, 0.28)`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: "18px",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  borderRadius: "20px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  padding: theme.spacing(3),
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: `0 15px 30px rgba(${THEME_BLUE_RGB}, 0.14)`,
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: "16px",
    padding: theme.spacing(2),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  background: peerCampusGradient,
  color: "white",
  borderRadius: "50px",
  textTransform: "none",
  fontWeight: 600,
  padding: "8px 20px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: `0 10px 25px rgba(${THEME_GREEN_RGB}, 0.22)`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: "8px 14px",
    fontSize: "0.875rem",
  },
}));

const EventRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.3s ease",
  "&:hover": {
    background: `rgba(${THEME_BLUE_RGB}, 0.04)`,
    transform: "translateX(5px)",
  },
  "& .MuiTableCell-root": {
    borderBottom: `1px solid rgba(${THEME_BLUE_RGB}, 0.08)`,
  },
}));

export default function ClubMemberEvents({ embedded = false } = {}) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [viewMode, setViewMode] = useState("table"); 
  const navigate = useNavigate();
  const location = useLocation();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.results)) return value.results;
    if (Array.isArray(value?.data)) return value.data;
    return [];
  };

  const splitDateTime = (dateTimeValue, fallbackTime = "") => {
    const raw = (dateTimeValue || "").toString();
    if (raw.includes('T')) {
      const [datePart, timePart] = raw.split('T');
      return {
        date: datePart,
        time: (timePart || "").slice(0, 5),
      };
    }
    return {
      date: raw,
      time: (fallbackTime || "").toString().slice(0, 5),
    };
  };

  const formatTime12h = (timeValue) => {
    const raw = (timeValue || "").toString().trim();
    if (!raw) return "";

    const match = raw.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return raw;

    const hour24 = Number(match[1]);
    const minute = match[2];
    if (Number.isNaN(hour24)) return raw;

    const isPM = hour24 >= 12;
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const suffix = isPM ? "PM" : "AM";
    return `${hour12}:${minute} ${suffix}`;
  };

  const buildTimeOptions = (stepMinutes = 30) => {
    const step = Math.max(1, Number(stepMinutes) || 30);
    const options = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += step) {
        const hh = String(hour).padStart(2, '0');
        const mm = String(minute).padStart(2, '0');
        const value24 = `${hh}:${mm}`;
        options.push({ value: value24, label: formatTime12h(value24) });
      }
    }
    return options;
  };

  const timeOptions = buildTimeOptions(30);

  // Sidebar items
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <ProfileIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsResponse, clubsResponse] = await Promise.all([
          api.get("events/event/"),
          api.get("events/club_name/"),
        ]);
        setEvents(normalizeArray(eventsResponse));
        setClubs(normalizeArray(clubsResponse));
      } catch (error) {
        console.error("Error fetching data:", error);
        showSnackbar("Failed to load data", "error");
      }
    };
    fetchData();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Calculate metrics
  const metrics = {
    total: events.length,
    active: events.filter(e => e.status === 'open').length,
    upcoming: events.filter(e => new Date(e.date) > new Date()).length,
    participants: events.reduce((sum, e) => sum + (e.participants || 0), 0),
  };

  const handleEdit = (event) => {
    setSelectedEvent({
      ...event,
      date: event.date || "",
      time: (event.time || "").toString().slice(0, 5),
      club_name: event.club_name || event.club || "",
    });
    setIsEdit(true);
    setOpen(true);
  };

  const handleAdd = () => {
    const defaultClubName = clubs.length > 0
      ? (clubs[0]?.name || clubs[0])
      : "";
    setSelectedEvent({
      title: "",
      details: "",
      date: "",
      time: "",
      location: "",
      link: "",
      type: "solo",
      status: "open",
      club_name: defaultClubName,
      image: null,
      capacity: 100,
      tags: [],
    });
    setIsEdit(false);
    setOpen(true);
  };

  const validateEvent = () => {
    if (!selectedEvent) {
      showSnackbar("Nothing to save", "error");
      return false;
    }
    const required = ["title", "details", "date", "time", "location", "club_name"];
    for (const field of required) {
      if (!selectedEvent[field] || selectedEvent[field].toString().trim() === "") {
        const label = field === "club_name" ? "Club" : field.charAt(0).toUpperCase() + field.slice(1);
        showSnackbar(`${label} is required`, "error");
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    try {
      if (!validateEvent()) return;
      if (clubs.length === 0) {
        showSnackbar("No clubs available to assign event", "error");
        return;
      }

      const { date: dateOnly, time: timeOnly } = splitDateTime(selectedEvent.date, selectedEvent.time);

      const normalizeUrl = (value) => {
        const raw = (value || "").toString().trim();
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        return `https://${raw}`;
      };

      const normalizedLink = normalizeUrl(selectedEvent.link);

      let response;

      if (isEdit && !(selectedEvent.image instanceof File)) {
        const eventData = {
          title: selectedEvent.title,
          details: selectedEvent.details,
          date: dateOnly,
          location: selectedEvent.location,
          link: normalizedLink,
          type: selectedEvent.type,
          status: selectedEvent.status,
          club_name: selectedEvent.club_name,
          time: timeOnly || null,
        };

        response = await api.put(`events/event-rduv/${selectedEvent.id}/`, eventData);
        const updated = response?.data?.data || response?.data || response;
        setEvents((prev) => prev.map((e) => (e.id === selectedEvent.id ? updated : e)));
        showSnackbar("Event updated successfully ✅");
      } else {
        const formData = new FormData();
        formData.append("title", selectedEvent.title);
        formData.append("details", selectedEvent.details || "");
        formData.append("date", dateOnly);
        formData.append("location", selectedEvent.location);
        formData.append("link", normalizedLink || "");
        formData.append("type", selectedEvent.type);
        formData.append("status", selectedEvent.status);
        formData.append("club_name", selectedEvent.club_name);
        if (timeOnly) {
          formData.append("time", timeOnly);
        }

        if (selectedEvent.image instanceof File) {
          formData.append("image", selectedEvent.image);
        }

        if (isEdit) {
          response = await api.put(`events/event-rduv/${selectedEvent.id}/`, formData);
          const updated = response?.data?.data || response?.data || response;
          setEvents((prev) => prev.map((e) => (e.id === selectedEvent.id ? updated : e)));
          showSnackbar("Event updated successfully with image ✅");
        } else {
          response = await api.post("events/event-create-view/", formData);
          const created = response?.data?.data || response?.data || response;
          setEvents((prev) => [...prev, created]);
          showSnackbar("Event created successfully 🚀");
        }
      }

      setOpen(false);
    } catch (error) {
      console.error("Error saving event:", error);
      const backendMsg = (() => {
        if (typeof error === 'string') return error;
        if (error?.message) return error.message;
        if (error?.error) return error.error;
        if (error?.detail) return error.detail;
        if (error?.msg) return error.msg;

        // DRF field error object: { field: ["msg"] }
        if (error && typeof error === 'object') {
          const entries = Object.entries(error);
          if (entries.length > 0) {
            return entries
              .map(([k, v]) => {
                if (Array.isArray(v)) return `${k}: ${v.join(' ')}`;
                if (typeof v === 'string') return `${k}: ${v}`;
                try {
                  return `${k}: ${JSON.stringify(v)}`;
                } catch {
                  return `${k}: ${String(v)}`;
                }
              })
              .join(' | ');
          }
        }

        return null;
      })();

      showSnackbar(backendMsg || "Failed to save event ❌", "error");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await api.delete(`events/event-rduv/${id}/`);
        setEvents((prev) => prev.filter((e) => e.id !== id));
        showSnackbar("Event deleted successfully");
      } catch (error) {
        console.error("Error deleting event:", error);
        showSnackbar("Failed to delete event", "error");
      }
    }
  };

  const handleView = (id) => {
    const path = String(location?.pathname || '').toLowerCase();
    if (path.startsWith('/clubmember/')) {
      navigate(`/clubmember/event/${id}`);
      return;
    }
    if (path.startsWith('/student/')) {
      navigate(`/student/event/${id}`);
      return;
    }
    navigate(`/event/${id}`);
  };

  const handleChange = (e) => {
    setSelectedEvent({ ...selectedEvent, [e.target.name]: e.target.value });
  };

  // Event Card for Mobile/Card View
  const EventCard = ({ event, index }) => (
    <Slide direction="up" in style={{ transitionDelay: `${index * 100}ms` }}>
      <Card 
        sx={{ 
          mb: 3, 
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "20px",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: `0 20px 40px rgba(${THEME_BLUE_RGB}, 0.14)`,
          },
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: THEME_BLUE, flex: 1 }}>
              {event.title}
            </Typography>
            <Chip 
              label={event.status} 
              size="small" 
              sx={{ 
                background: event.status === 'open' ? THEME_GREEN : THEME_BLUE,
                color: "white",
                fontWeight: 600,
                ml: 1,
              }} 
            />
          </Box>
          
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthIcon sx={{ color: THEME_BLUE, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>
                {event.date}{event.time ? ` • ${formatTime12h(event.time)}` : ""}
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationOnIcon sx={{ color: THEME_BLUE, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>
                {event.location}
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon sx={{ color: THEME_BLUE, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>
                {event.club}
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventAvailableIcon sx={{ color: THEME_BLUE, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)`, textTransform: "capitalize" }}>
                {event.type}
              </Typography>
            </Box>
          </Stack>

          {/* Attendance Progress */}
          {event.capacity && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.62)` }}>
                  Attendance
                </Typography>
                <Typography variant="caption" sx={{ color: THEME_BLUE, fontWeight: 700 }}>
                  {event.participants || 0} / {event.capacity}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={((event.participants || 0) / event.capacity) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                  "& .MuiLinearProgress-bar": {
                    background: peerCampusGradient,
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton 
                size="small" 
                onClick={() => handleEdit(event)}
                sx={{ 
                  background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                  color: THEME_BLUE,
                  "&:hover": { background: `rgba(${THEME_BLUE_RGB}, 0.18)` }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => handleView(event.id)}
                sx={{ 
                  background: `rgba(${THEME_GREEN_RGB}, 0.12)`,
                  color: THEME_GREEN,
                  "&:hover": { background: `rgba(${THEME_GREEN_RGB}, 0.18)` }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => handleDelete(event.id)}
                sx={{ 
                  background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                  color: THEME_BLUE,
                  "&:hover": { background: `rgba(${THEME_BLUE_RGB}, 0.18)` }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Button
              size="small"
              variant="outlined"
              href={event.link}
              target="_blank"
              sx={{
                borderRadius: "50px",
                borderColor: THEME_BLUE,
                color: THEME_BLUE,
                fontSize: "0.75rem",
                px: 2,
              }}
            >
              View Link
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Slide>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: embedded ? "auto" : "100vh",
        background: embedded ? "transparent" : `rgba(${THEME_BLUE_RGB}, 0.03)`,
      }}
    >
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}

      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {!embedded && !isMobile && <GlobalSidebar />}

        <Box
          sx={{
            flex: 1,
            p: embedded ? 0 : (isSmallMobile ? 1.5 : (isMobile ? 2 : 3)),
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <HeaderCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3, mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                {!embedded && (
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    size={isSmallMobile ? "small" : "medium"}
                    sx={{
                      borderRadius: "50px",
                      borderColor: "rgba(255,255,255,0.3)",
                      color: "white",
                      background: "rgba(255,255,255,0.1)",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.6)",
                        background: "rgba(255,255,255,0.2)",
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Box>
                  <Typography
                    variant={isSmallMobile ? "h5" : "h4"}
                    sx={{
                      fontWeight: 800,
                      mb: 0.5,
                    }}
                  >
                    Event Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage and organize all your campus events
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={viewMode === "card"}
                      onChange={(e) => setViewMode(e.target.checked ? "card" : "table")}
                      sx={{
                        "& .MuiSwitch-track": {
                          background: "rgba(255,255,255,0.3)",
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DashboardIcon fontSize="small" />
                      <Typography sx={{ color: "white", fontSize: "0.875rem" }}>
                        Card View
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
                <ActionButton
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  size={isSmallMobile ? "small" : "medium"}
                >
                  New Event
                </ActionButton>
              </Box>
            </Box>

            {/* Metrics */}
            <Grid container spacing={3}>
              {[
                { label: "Total Events", value: metrics.total, icon: <EventAvailableIcon />, color: THEME_BLUE },
                { label: "Active Events", value: metrics.active, icon: <PeopleIcon />, color: THEME_GREEN },
                { label: "Upcoming", value: metrics.upcoming, icon: <CalendarMonthIcon />, color: THEME_BLUE },
                { label: "Total Participants", value: metrics.participants, icon: <PeopleIcon />, color: THEME_GREEN },
              ].map((metric, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <MetricCard>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box sx={{
                        background: `${metric.color}20`,
                        borderRadius: "12px",
                        p: 1,
                        mr: 2,
                        color: metric.color,
                      }}>
                        {metric.icon}
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ 
                          fontWeight: 800,
                          color: THEME_BLUE,
                          lineHeight: 1,
                        }}>
                          {metric.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.62)` }}>
                          {metric.label}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
              ))}
            </Grid>
          </HeaderCard>

          {/* Events List */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, color: THEME_BLUE, fontWeight: 700 }}>
              All Events ({events.length})
            </Typography>
            
            {!isMobile && viewMode === "table" ? (
              // Desktop Table View
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "20px",
                  boxShadow: `0 10px 30px rgba(${THEME_BLUE_RGB}, 0.10)`,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  overflow: 'auto'
                }}
              >
                <Table>
                  <TableHead sx={{ bgcolor: `rgba(${THEME_BLUE_RGB}, 0.04)` }}>
                    <TableRow>
                      {["Event", "Date", "Time", "Location", "Type", "Status", "Participants", "Actions"].map((head) => (
                        <TableCell
                          key={head}
                          align={head === "Actions" ? "center" : "left"}
                          sx={{
                            color: THEME_BLUE,
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            py: 2,
                            borderBottom: `2px solid rgba(${THEME_BLUE_RGB}, 0.14)`,
                          }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((event, index) => (
                      <EventRow key={event.id}>
                        <TableCell sx={{ color: THEME_BLUE, fontWeight: 700 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {getFullImageUrl(event.image) && (
                              <Avatar
                                src={getFullImageUrl(event.image)}
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              />
                            )}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {event.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.62)` }}>
                                {event.club}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>{event.date}</TableCell>
                        <TableCell sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>{event.time ? formatTime12h(event.time) : "—"}</TableCell>
                        <TableCell sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>{event.location}</TableCell>
                        <TableCell>
                          <Chip
                            label={event.type}
                            size="small"
                            sx={{
                              background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                              color: THEME_BLUE,
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.status}
                            size="small"
                            sx={{
                              background: event.status === 'open'
                                ? `rgba(${THEME_GREEN_RGB}, 0.14)`
                                : `rgba(${THEME_BLUE_RGB}, 0.10)`,
                              color: event.status === 'open' ? THEME_GREEN : THEME_BLUE,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.72)` }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PeopleIcon sx={{ fontSize: 16, color: THEME_BLUE }} />
                            <Typography variant="body2">
                              {event.participants || 0}/{event.capacity || 100}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(event)}
                              sx={{ 
                                background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                                color: THEME_BLUE,
                                "&:hover": { background: `rgba(${THEME_BLUE_RGB}, 0.18)` }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(event.id)}
                              sx={{ 
                                background: `rgba(${THEME_GREEN_RGB}, 0.12)`,
                                color: THEME_GREEN,
                                "&:hover": { background: `rgba(${THEME_GREEN_RGB}, 0.18)` }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(event.id)}
                              sx={{ 
                                background: `rgba(${THEME_BLUE_RGB}, 0.10)`,
                                color: THEME_BLUE,
                                "&:hover": { background: `rgba(${THEME_BLUE_RGB}, 0.18)` }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </EventRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              // Mobile/Card View
              <Box>
                {events.map((event, index) => (
                  <EventCard key={event.id} event={event} index={index} />
                ))}
              </Box>
            )}
          </Box>

          {/* Add/Edit Dialog */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            fullScreen={isSmallMobile}
            PaperProps={{
              sx: {
                borderRadius: isSmallMobile ? 0 : "24px",
                background: "white",
                maxHeight: isSmallMobile ? '100%' : '90vh'
              },
            }}
          >
            <DialogTitle sx={{ 
              fontWeight: 700, 
              textAlign: "center",
              pb: 1,
              background: peerCampusGradient,
              color: "white",
              borderRadius: isSmallMobile ? 0 : "24px 24px 0 0",
            }}>
              {isEdit ? "✏️ Edit Event" : "🚀 Create New Event"}
            </DialogTitle>
            <DialogContent
              dividers={isSmallMobile}
              sx={{
                pt: isSmallMobile ? 2 : 3,
                pb: 2,
                px: isSmallMobile ? 2 : 3,
                overflowY: 'auto',
              }}
            >
              <Grid container spacing={isSmallMobile ? 1.5 : 2}>
                {/* Title */}
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Event Title"
                    name="title"
                    fullWidth
                    value={selectedEvent?.title || ""}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Date & Location */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Event Date"
                    name="date"
                    type="date"
                    fullWidth
                    value={selectedEvent?.date || ""}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Event Time"
                    name="time"
                    fullWidth
                    select
                    value={selectedEvent?.time || ""}
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: 320 }
                        }
                      }
                    }}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    <MenuItem value="" disabled sx={{ color: `rgba(${THEME_BLUE_RGB}, 0.62)` }}>
                      Select time
                    </MenuItem>
                    {timeOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value} sx={{ color: THEME_BLUE }}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Location"
                    name="location"
                    fullWidth
                    value={selectedEvent?.location || ""}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Club & Type */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Club"
                    name="club_name"
                    fullWidth
                    select
                    value={selectedEvent?.club_name || ""}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    {clubs.map((club) => {
                      const clubValue = club?.name || club;
                      const clubKey = club?.id || clubValue;
                      return (
                        <MenuItem key={clubKey} value={clubValue} sx={{ color: THEME_BLUE }}>
                          {clubValue}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Event Type"
                    name="type"
                    fullWidth
                    select
                    value={selectedEvent?.type || "solo"}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    {[
                      
                      { value: "tech", label: "Tech" },
                      { value: "cultural", label: "Cultural" },
                      { value: "sports", label: "Sports" }
                    ].map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ color: THEME_BLUE }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Status & Capacity */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Status"
                    name="status"
                    fullWidth
                    select
                    value={selectedEvent?.status || "open"}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    <MenuItem sx={{ color: THEME_BLUE }} value="open">
                      Open
                    </MenuItem>
                    <MenuItem sx={{ color: THEME_BLUE }} value="closed">
                      Closed
                    </MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Capacity"
                    name="capacity"
                    type="number"
                    fullWidth
                    value={selectedEvent?.capacity || 100}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Registration Link */}
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Registration Link"
                    name="link"
                    type="url"
                    fullWidth
                    value={selectedEvent?.link || ""}
                    onChange={handleChange}
                    helperText="Example: https://example.com"
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Event Description"
                    name="details"
                    fullWidth
                    multiline
                    rows={isSmallMobile ? 3 : 4}
                    value={selectedEvent?.details || ""}
                    onChange={handleChange}
                    slotProps={{
                      inputLabel: {
                        sx: { color: `rgba(${THEME_BLUE_RGB}, 0.62)` }
                      },
                      input: {
                        sx: { color: THEME_BLUE }
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Image Upload */}
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<AddIcon />}
                    sx={{ 
                      mt: 1, 
                      borderRadius: "50px",
                      borderColor: THEME_BLUE, 
                      color: THEME_BLUE,
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                        background: `rgba(${THEME_BLUE_RGB}, 0.04)`,
                      }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    Upload Event Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedEvent({ ...selectedEvent, image: file });
                          showSnackbar("Image selected for upload", "success");
                        }
                      }}
                    />
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ 
              justifyContent: "center", 
              pb: isSmallMobile ? 2 : 3,
              pt: 2,
              gap: 2,
              flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
            }}>
              <Button 
                onClick={() => setOpen(false)} 
                sx={{ 
                  borderRadius: "50px", 
                  px: isSmallMobile ? 3 : 4, 
                  color: THEME_BLUE, 
                  border: `2px solid rgba(${THEME_BLUE_RGB}, 0.22)`,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: `rgba(${THEME_BLUE_RGB}, 0.36)`,
                    background: `rgba(${THEME_BLUE_RGB}, 0.04)`,
                  }
                }}
                size={isSmallMobile ? "small" : "medium"}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSave} 
                sx={{ 
                  borderRadius: "50px", 
                  px: isSmallMobile ? 3 : 4, 
                  background: peerCampusGradient,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: `0 5px 20px rgba(${THEME_BLUE_RGB}, 0.22)`,
                  "&:hover": {
                    boxShadow: `0 8px 25px rgba(${THEME_BLUE_RGB}, 0.28)`,
                  }
                }}
                size={isSmallMobile ? "small" : "medium"}
              >
                {isEdit ? "Save Changes" : "Create Event"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={6000} 
            onClose={handleCloseSnackbar} 
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity} 
              sx={{ 
                width: "100%",
                borderRadius: "12px",
                boxShadow: `0 10px 30px rgba(${THEME_BLUE_RGB}, 0.16)`,
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {!embedded && (
            <Box sx={{ mt: 6 }}>
              <PostLoginFooter />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}